import React, { useMemo } from 'react';

import { Typography, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import RichTextContent from 'components/RichTextContent/RichTextContent';
import type { ActionTemplate } from 'config/api/actionLibrary/actionLibrary.types';
import action_library_messages from 'messages/action_library_messages';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(4),
    '@media print': {
      padding: theme.spacing(2),
    },
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 600,
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  label: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  value: {
    marginBottom: theme.spacing(1),
  },
  stepContainer: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    pageBreakInside: 'avoid',
  },
  stepTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  stepContent: {
    marginBottom: theme.spacing(1),
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  chip: {
    border: '1px solid #e0e0e0',
    borderRadius: 16,
    padding: '2px 8px',
    fontSize: '0.75rem',
    backgroundColor: '#f5f5f5',
    color: 'rgba(0, 0, 0, 0.87)',
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  criticalityLowIcon: {
    color: theme.palette.info.main,
  },
  criticalityMediumIcon: {
    color: theme.palette.warning.main,
  },
  criticalityHighIcon: {
    color: theme.palette.error.main,
  },
  valueWithIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    verticalAlign: 'middle',
  },
  statusIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: '1.2rem',
  },
}));

type Props = {
  template: ActionTemplate;
};

const ActionTemplatePrintTemplate: React.FC<Props> = ({ template }) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const criticalityData = useMemo(() => {
    const criticalityKey = template.criticality.toUpperCase() as 'LOW' | 'MEDIUM' | 'HIGH';
    const displayValue = template.criticalityDisplay || t(action_library_messages.criticality[criticalityKey]);
    let IconComponent;
    let iconClass;

    if (template.criticality === 'low') {
      IconComponent = InfoIcon;
      iconClass = styles.criticalityLowIcon;
    } else if (template.criticality === 'medium') {
      IconComponent = WarningIcon;
      iconClass = styles.criticalityMediumIcon;
    } else {
      IconComponent = ErrorIcon;
      iconClass = styles.criticalityHighIcon;
    }

    return {
      displayValue,
      IconComponent,
      iconClass,
    };
  }, [template, t]);

  return (
    <div className={styles.root}>
      <Typography className={styles.title} variant='h3'>
        {template.name}
      </Typography>

      <div className={styles.section}>
        <div className={styles.value}>{template.shortDescription ? <RichTextContent content={template.shortDescription} /> : '-'}</div>
      </div>

      <Grid container spacing={4}>
        <Grid item xs={6}>
          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.table.purpose)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              {template.purposeDisplay || template.purpose}
            </Typography>
          </div>

          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.table.area)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              {(template.areaData?.displayName as string) || template.areaDisplay || template.area}
            </Typography>
          </div>

          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.detail.reported_to)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              {template.reportedTo || '-'}
            </Typography>
          </div>

          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.detail.updated_at)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              {format(new Date(template.updatedAt), 'yyyy-MM-dd HH:mm')}
            </Typography>
          </div>
        </Grid>

        <Grid item xs={6}>
          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.table.criticality)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              <criticalityData.IconComponent className={`${styles.statusIcon} ${criticalityData.iconClass}`} />
              {criticalityData.displayValue}
            </Typography>
          </div>
          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.table.subarea)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              {(template.subAreaData?.displayName as string) || template.subArea || '-'}
            </Typography>
          </div>

          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.detail.created_at)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              {format(new Date(template.createdAt), 'yyyy-MM-dd HH:mm')}
            </Typography>
          </div>

          {template.ownerType === 'organization' && template.ownerOrganization && (
            <div className={styles.section}>
              <Typography className={styles.label} variant='body2'>
                {t(action_library_messages.detail.owner_organization)}
              </Typography>
              <Typography className={styles.value} variant='body1'>
                {template.ownerOrganization}
              </Typography>
            </div>
          )}

          {template.createdBy && (
            <div className={styles.section}>
              <Typography className={styles.label} variant='body2'>
                {t(action_library_messages.detail.created_by)}
              </Typography>
              <Typography className={styles.value} variant='body1'>
                {template.createdBy.fullName}
              </Typography>
            </div>
          )}
        </Grid>
      </Grid>

      {template.reference && (
        <>
          <hr />
          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.detail.reference)}
            </Typography>
            <div className={styles.chipContainer}>
              {template.reference.split(', ').map((ref, index) => (
                <div key={index} className={styles.chip}>
                  {ref}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {template.assetCategories && template.assetCategories.length > 0 && (
        <>
          <hr />
          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.detail.asset_types)}
            </Typography>
            <div className={styles.chipContainer}>
              {template.assetCategories.map(category => (
                <div key={category.id} className={styles.chip}>
                  {category.name}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {template.tags && typeof template.tags === 'object' && Object.keys(template.tags).length > 0 && (
        <>
          <hr />
          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.detail.tags)}
            </Typography>
            <div className={styles.chipContainer}>
              {Object.entries(template.tags).map(([key, value]) => (
                <div key={key} className={styles.chip}>
                  {key}: {String(value).toLowerCase()}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {template.validityPeriodMonths && (
        <>
          <hr />
          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.detail.validity_period)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              {template.validityPeriodMonths
                ? `${template.validityPeriodMonths} ${template.validityPeriodMonths === 1 ? 'month' : 'months'}`
                : '-'}
            </Typography>
          </div>
        </>
      )}

      {template.steps && template.steps.length > 0 && (
        <>
          <hr />
          <div className={styles.section}>
            <Typography className={styles.title} variant='h5'>
              {t(action_library_messages.detail.steps)}
            </Typography>
            {template.steps
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((step, index) => (
                <div key={step.id} className={styles.stepContainer}>
                  <Typography className={styles.stepTitle} variant='subtitle1'>
                    {index + 1}. {step.title}
                    {step.mandatory && (
                      <span
                        style={{
                          marginLeft: 8,
                          border: '1px solid #e0e0e0',
                          borderRadius: 16,
                          padding: '2px 8px',
                          fontSize: '0.75rem',
                          fontWeight: 'normal',
                          display: 'inline-block',
                          verticalAlign: 'middle',
                        }}
                      >
                        Mandatory
                      </span>
                    )}
                  </Typography>
                  <div className={styles.stepContent}>
                    <RichTextContent content={step.shortDescription} />
                  </div>
                  <div className={styles.chipContainer}>
                    {step.reference && <div className={styles.chip}>Reference: {step.reference}</div>}
                    {step.relevantFor && <div className={styles.chip}>Relevant For: {step.relevantFor}</div>}
                  </div>
                  {step.tags && typeof step.tags === 'object' && Object.keys(step.tags).length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Typography variant='caption'>Tags:</Typography>
                      <div className={styles.chipContainer}>
                        {Object.entries(step.tags).map(([key, value]) => (
                          <div key={key} className={styles.chip}>
                            {key}: {String(value).toLowerCase()}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ActionTemplatePrintTemplate;
